import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, Heart, TrendingUp, Users, Brain, MessageSquare,
  Calendar, MapPin, Star, AlertCircle, CheckCircle, Target,
  Lightbulb, Award, Crown, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const CompatibilityReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');

  useEffect(() => {
    fetchCompatibilityData();
  }, []);

  const fetchCompatibilityData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🔄 Fetching compatibility data...');
      
      const [reportsRes, matchesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/compatibility/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/compatibility/potential-matches`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log('✅ Compatibility data loaded:', { 
        reports: reportsRes.data.reports?.length, 
        matches: matchesRes.data.potential_matches?.length 
      });

      setReports(reportsRes.data.reports || []);
      setPotentialMatches(matchesRes.data.potential_matches || []);
    } catch (error) {
      console.error('❌ Error fetching compatibility data:', error);
      if (error.response?.status === 402) {
        navigate('/subscription/plans');
      } else {
        setError('Unable to load compatibility data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (targetUserId) => {
    try {
      setGenerating(targetUserId);
      const token = localStorage.getItem('token');

      console.log('🎯 Generating compatibility report for user:', targetUserId);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compatibility/generate`,
        { target_user_id: targetUserId },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      console.log('✅ Compatibility report generated:', response.data);
      
      // Refresh reports list
      await fetchCompatibilityData();
      
      // Show the newly generated report
      setSelectedReport(response.data.report);
      setActiveTab('reports');

    } catch (error) {
      console.error('❌ Error generating report:', error);
      setError('Failed to generate compatibility report. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const formatCompatibilityScore = (score) => {
    if (score >= 80) return { level: 'Excellent', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    if (score >= 65) return { level: 'Good', color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    if (score >= 50) return { level: 'Moderate', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
    return { level: 'Low', color: 'text-red-400', bgColor: 'bg-red-500/20' };
  };

  const getCategoryIcon = (category) => {
    const icons = {
      demographics: MapPin,
      interests: Heart,
      lifestyle: Users,
      values: Target,
      communication: MessageSquare
    };
    return icons[category] || Heart;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-400 mx-auto mb-4"></div>
              <p className="text-white">Loading your compatibility reports...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-6xl mx-auto">
          <Button
            onClick={() => navigate('/coaching/dashboard')}
            variant="outline"
            className="mb-6 border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Unable to Load Reports</h2>
              <p className="text-gray-300 mb-4">{error}</p>
              <Button onClick={fetchCompatibilityData} className="bg-rose-600 hover:bg-rose-700">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/coaching/dashboard')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center space-x-3">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white">Relationship Compatibility Reports</h1>
              <p className="text-gray-300 text-sm">Deep insights into your relationship potential</p>
            </div>
            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Crown className="w-4 h-4 mr-1" />
              VIP Feature
            </Badge>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8">
          <Button
            onClick={() => setActiveTab('reports')}
            variant={activeTab === 'reports' ? 'default' : 'outline'}
            className={activeTab === 'reports' ? 'bg-rose-600' : 'border-white/20 text-white hover:bg-white/10'}
          >
            <Brain className="w-4 h-4 mr-2" />
            Your Reports ({reports.length})
          </Button>
          <Button
            onClick={() => setActiveTab('matches')}
            variant={activeTab === 'matches' ? 'default' : 'outline'}
            className={activeTab === 'matches' ? 'bg-rose-600' : 'border-white/20 text-white hover:bg-white/10'}
          >
            <Users className="w-4 h-4 mr-2" />
            Potential Matches ({potentialMatches.length})
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {selectedReport ? (
              /* Detailed Report View */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => setSelectedReport(null)}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Reports
                  </Button>
                  
                  <Badge className={formatCompatibilityScore(selectedReport.overall_compatibility).bgColor}>
                    <span className={formatCompatibilityScore(selectedReport.overall_compatibility).color}>
                      {selectedReport.overall_compatibility}% {formatCompatibilityScore(selectedReport.overall_compatibility).level}
                    </span>
                  </Badge>
                </div>

                {/* Overall Assessment */}
                <Card className="bg-white/5 backdrop-blur-md border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Heart className="w-5 h-5 mr-2 text-rose-400" />
                      Overall Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 leading-relaxed">
                      {selectedReport.detailed_analysis?.overall_assessment}
                    </p>
                  </CardContent>
                </Card>

                {/* Compatibility Breakdown */}
                <Card className="bg-white/5 backdrop-blur-md border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Compatibility Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {Object.entries(selectedReport.compatibility_breakdown || {}).map(([category, score]) => {
                        if (category === 'overall') return null;
                        const IconComponent = getCategoryIcon(category);
                        const scoreFormat = formatCompatibilityScore(score);
                        
                        return (
                          <div key={category} className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <IconComponent className="w-4 h-4 text-rose-400" />
                                <span className="text-white capitalize">{category.replace('_', ' ')}</span>
                              </div>
                              <span className={scoreFormat.color}>{score}%</span>
                            </div>
                            <Progress value={score} className="h-2" />
                            <p className="text-sm text-gray-400">
                              {selectedReport.detailed_analysis?.category_insights?.[category]}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                {selectedReport.recommendations && selectedReport.recommendations.length > 0 && (
                  <Card className="bg-white/5 backdrop-blur-md border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center">
                        <Lightbulb className="w-5 h-5 mr-2 text-yellow-400" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {selectedReport.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-300">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Relationship Strengths & Challenges */}
                <div className="grid md:grid-cols-2 gap-6">
                  {selectedReport.strengths && selectedReport.strengths.length > 0 && (
                    <Card className="bg-white/5 backdrop-blur-md border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <Award className="w-5 h-5 mr-2 text-green-400" />
                          Relationship Strengths
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {selectedReport.strengths.map((strength, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <Star className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-300">{strength}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {selectedReport.potential_challenges && selectedReport.potential_challenges.length > 0 && (
                    <Card className="bg-white/5 backdrop-blur-md border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center">
                          <AlertCircle className="w-5 h-5 mr-2 text-orange-400" />
                          Areas to Navigate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {selectedReport.potential_challenges.map((challenge, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <AlertCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-300">{challenge}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            ) : (
              /* Reports List View */
              <div className="space-y-4">
                {reports.length > 0 ? (
                  reports.map((report) => {
                    const scoreFormat = formatCompatibilityScore(report.overall_compatibility);
                    return (
                      <Card 
                        key={report.report_id} 
                        className="bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all cursor-pointer"
                        onClick={() => setSelectedReport(report)}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`w-12 h-12 rounded-full ${scoreFormat.bgColor} flex items-center justify-center`}>
                                <span className={`font-bold ${scoreFormat.color}`}>
                                  {report.overall_compatibility}%
                                </span>
                              </div>
                              <div>
                                <h3 className="text-white font-semibold">
                                  Compatibility Report #{report.report_id.slice(-6)}
                                </h3>
                                <p className="text-gray-400 text-sm flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {new Date(report.generated_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className={scoreFormat.bgColor}>
                                <span className={scoreFormat.color}>
                                  {scoreFormat.level} Match
                                </span>
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Card className="bg-white/5 backdrop-blur-md border-white/10">
                    <CardContent className="p-8 text-center">
                      <Brain className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">No Reports Yet</h3>
                      <p className="text-gray-300 mb-6">
                        Generate your first compatibility report by analyzing potential matches!
                      </p>
                      <Button
                        onClick={() => setActiveTab('matches')}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Find Potential Matches
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-4">
            {potentialMatches.length > 0 ? (
              potentialMatches.map((match) => {
                const scoreFormat = formatCompatibilityScore(match.compatibility_score);
                return (
                  <Card key={match.user_id} className="bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Profile Photo */}
                          <div className="relative">
                            {match.photo_url ? (
                              <img 
                                src={match.photo_url} 
                                alt={match.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white text-xl font-bold">
                                  {match.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            {/* Compatibility Score Badge */}
                            <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full ${scoreFormat.bgColor} flex items-center justify-center border-2 border-slate-900`}>
                              <span className={`text-xs font-bold ${scoreFormat.color}`}>
                                {match.compatibility_score}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-semibold text-lg">{match.name}</h3>
                              {/* Source Badge */}
                              <Badge className={match.source === 'connection' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-purple-500/20 text-purple-400 border-purple-500/30'}>
                                {match.source === 'connection' ? 'Connection' : 'Speed Date'}
                              </Badge>
                            </div>
                            <p className="text-gray-400 text-sm">
                              {match.age ? `${match.age} years old` : ''}
                              {match.age && match.location ? ' • ' : ''}
                              {match.location && <><MapPin className="w-3 h-3 inline mr-1" />{match.location}</>}
                            </p>
                            {match.bio && (
                              <p className="text-gray-300 text-sm mt-1 line-clamp-2">
                                {match.bio}
                              </p>
                            )}
                            {match.top_compatibility_areas && match.top_compatibility_areas.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {match.top_compatibility_areas.slice(0, 3).map((area) => (
                                  <Badge key={area} className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {area.replace('_', ' ')}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right space-y-2">
                          <div className="mb-2">
                            <Badge className={scoreFormat.bgColor}>
                              <span className={scoreFormat.color}>
                                {scoreFormat.level} Match
                              </span>
                            </Badge>
                          </div>
                          <Button
                            onClick={() => generateReport(match.user_id)}
                            disabled={generating === match.user_id}
                            className="bg-rose-600 hover:bg-rose-700"
                            data-testid={`generate-report-${match.user_id}`}
                          >
                            {generating === match.user_id ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Brain className="w-4 h-4 mr-2" />
                            )}
                            Generate Report
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-8 text-center">
                  <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Connections Yet</h3>
                  <p className="text-gray-300 mb-6">
                    Start making connections through speed dating or matching to analyze compatibility!
                  </p>
                  <div className="flex justify-center gap-4">
                    <Button
                      onClick={() => navigate('/speed-dating-session')}
                      className="bg-rose-600 hover:bg-rose-700"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Start Speed Dating
                    </Button>
                    <Button
                      onClick={() => navigate('/matches')}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      View Matches
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompatibilityReports;